'use client';

/**
 * First Login Chat Hook
 *
 * Loads the personalized greeting when the user opens chat, not on
 * shell mount (avoids POST /api/protected/chat/greeting on the
 * dashboard first-paint burst).
 *
 * Storage strategy:
 * - First login: localStorage (permanent until reset)
 * - Session greeting: localStorage stores Clerk session ID — a new session
 *   automatically re-triggers the greeting without needing explicit clearing
 */
import { useEffect, useRef } from 'react';

import { useAuth, useUser } from '@clerk/nextjs';

import { useFingerprintRequestId } from '@/lib/fingerprint/hooks/use-fingerprint-request-id';

import {
  useChatDialogActions,
  useChatDialogState,
} from '../providers/chat-dialog-provider';

const FIRST_LOGIN_KEY = 'clai_first_login_shown';
const GREETING_SHOWN_KEY = 'clai_greeting_shown';

export interface UseFirstLoginChatOptions {
  /** Whether to show on first login ever (uses localStorage) */
  showOnFirstLogin?: boolean;
  /** Whether to show on first login of session (uses localStorage) */
  showOnSessionStart?: boolean;
  /** Whether this hook is enabled */
  enabled?: boolean;
}

/**
 * Fetches and sets the FAB/panel greeting when chat opens and the
 * first-login / session-start gates pass.
 */
export function useFirstLoginChat(options: UseFirstLoginChatOptions = {}) {
  const {
    showOnFirstLogin = true,
    showOnSessionStart = false,
    enabled = true,
  } = options;

  const { user, isLoaded: isUserLoaded } = useUser();
  const { sessionId } = useAuth();
  const { isOpen } = useChatDialogState();
  const { setGreeting } = useChatDialogActions();
  const { getRequestId } = useFingerprintRequestId();
  const getRequestIdRef = useRef(getRequestId);
  getRequestIdRef.current = getRequestId;
  const inFlightRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!enabled || !isUserLoaded || !user?.id || !sessionId || !isOpen) {
      return;
    }

    const shouldShow = checkShouldShowChat(
      user.id,
      showOnFirstLogin,
      showOnSessionStart,
      sessionId
    );
    if (!shouldShow) return;

    inFlightRef.current?.abort('superseded');
    const abort = new AbortController();
    inFlightRef.current = abort;

    const firstName = user.firstName || 'there';
    const fallback = `Hey ${firstName}! I'm Clai, your AI assistant. I'm here to help with deals, documents, or anything you need.`;

    const fetchGreeting = async () => {
      const hardTimeout = setTimeout(() => abort.abort('timeout'), 5000);
      let greetingWasSet = false;

      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const fingerprintRequestId = await getRequestIdRef.current();

        const response = await fetch('/api/protected/chat/greeting', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ firstName, timezone, fingerprintRequestId }),
          signal: abort.signal,
        });

        if (!response.ok) throw new Error('Greeting API failed');

        const text = await response.text();
        if (abort.signal.aborted) return;

        setGreeting(text.trim() || fallback);
        greetingWasSet = true;
      } catch {
        if (
          abort.signal.reason !== 'cleanup' &&
          abort.signal.reason !== 'superseded'
        ) {
          setGreeting(fallback);
          greetingWasSet = true;
        }
      } finally {
        clearTimeout(hardTimeout);
      }

      if (greetingWasSet) {
        markChatAsShown(
          user.id,
          showOnFirstLogin,
          showOnSessionStart,
          sessionId
        );
      }
    };

    void fetchGreeting();

    return () => {
      abort.abort('cleanup');
    };
  }, [
    enabled,
    isUserLoaded,
    user?.id,
    user?.firstName,
    sessionId,
    isOpen,
    showOnFirstLogin,
    showOnSessionStart,
    setGreeting,
  ]);
}

/* ------------------------------------------------------------------ */
/*  Storage helpers                                                    */
/* ------------------------------------------------------------------ */

function checkShouldShowChat(
  userId: string,
  checkFirstLogin: boolean,
  checkSessionStart: boolean,
  sessionId: string | null | undefined
): boolean {
  try {
    if (checkFirstLogin) {
      const hasShown = localStorage.getItem(`${FIRST_LOGIN_KEY}_${userId}`);
      if (!hasShown) return true;
    }

    if (checkSessionStart && sessionId) {
      const storedSessionId = localStorage.getItem(
        `${GREETING_SHOWN_KEY}_${userId}`
      );
      if (storedSessionId !== sessionId) return true;
    }

    return false;
  } catch {
    return false;
  }
}

function markChatAsShown(
  userId: string,
  markFirstLogin: boolean,
  markSession: boolean,
  sessionId: string | null | undefined
): void {
  try {
    if (markFirstLogin) {
      localStorage.setItem(
        `${FIRST_LOGIN_KEY}_${userId}`,
        new Date().toISOString()
      );
    }
    if (markSession && sessionId) {
      localStorage.setItem(`${GREETING_SHOWN_KEY}_${userId}`, sessionId);
    }
  } catch {
    // Storage access failed
  }
}

/**
 * Clear the greeting-shown flag so the next login triggers a greeting.
 * Called from the signout hook.
 */
export function clearGreetingShownFlag(userId?: string): void {
  try {
    if (userId) {
      localStorage.removeItem(`${GREETING_SHOWN_KEY}_${userId}`);
    } else {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(GREETING_SHOWN_KEY)) {
          localStorage.removeItem(key);
        }
      });
    }
  } catch {
    // Storage access failed
  }
}
